import { useEffect, useState } from "react";
import axios from "axios";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AVATAR_COLORS = [
  "bg-emerald-600",
  "bg-violet-600",
  "bg-amber-600",
  "bg-rose-600",
  "bg-blue-600",
  "bg-teal-600",
];

function avatarColor(name) {
  let hash = 0;
  for (let i = 0; i < (name || "").length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

/**
 * Customer Reviews carousel — sits below the Featured products widget on
 * the Dealer Home right sidebar. Pulls reviews from the Airtable `Review`
 * table (featured first, then top-rated). One review per slide with
 * prev/next arrows, dot indicators and a "See all reviews →" link.
 */
export default function ReviewsSidebar({ dealerSlug }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/postcue/reviews?featured_only=true&limit=10`,
        );
        let list = data?.items || [];
        // Fall back to top-rated reviews if nothing is flagged "Featured".
        if (list.length === 0) {
          const { data: alt } = await axios.get(
            `${API}/postcue/reviews?min_rating=4&limit=10`,
          );
          list = alt?.items || [];
        }
        if (!cancel) setItems(list);
      } catch {
        if (!cancel) setItems([]);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  if (loading || items.length === 0) return null;

  const safeIdx = ((idx % items.length) + items.length) % items.length;
  const current = items[safeIdx];
  const initial = (current.name || "?").trim().charAt(0).toUpperCase();
  const reviewsHref = dealerSlug ? `/d/${dealerSlug}?view=reviews` : "/reviews";

  return (
    <div
      className="rounded-2xl border border-zinc-200 bg-white overflow-hidden"
      data-testid="reviews-sidebar"
    >
      <div className="px-5 pt-4 pb-3 border-b border-zinc-200">
        <h3 className="font-semibold text-zinc-900 text-lg tracking-tight">
          Customer Reviews
        </h3>
      </div>

      <div className="relative p-4">
        <div className="rounded-xl bg-zinc-50 border border-zinc-100 p-4">
          <div className="flex items-center gap-3 mb-3">
            <div
              className={`w-10 h-10 rounded-full ${avatarColor(
                current.name,
              )} text-white flex items-center justify-center text-sm font-semibold shrink-0`}
              aria-hidden="true"
            >
              {initial}
            </div>
            <div className="min-w-0">
              <div
                className="font-semibold text-zinc-900 text-sm truncate"
                data-testid="reviews-sidebar-name"
              >
                {current.name}
              </div>
              <div className="flex items-center gap-0.5 mt-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    size={12}
                    className={
                      i < (current.rating || 0)
                        ? "fill-amber-400 text-amber-400"
                        : "fill-zinc-200 text-zinc-200"
                    }
                  />
                ))}
              </div>
            </div>
          </div>
          <p
            className="text-sm text-zinc-700 leading-relaxed line-clamp-6"
            data-testid="reviews-sidebar-content"
          >
            {current.content}
          </p>
        </div>

        {items.length > 1 && (
          <>
            <button
              type="button"
              onClick={() => setIdx(safeIdx - 1)}
              className="absolute top-1/2 -left-1 -translate-y-1/2 w-9 h-9 rounded-full bg-white border border-zinc-200 shadow-md flex items-center justify-center text-zinc-600 hover:text-emerald-700 hover:border-emerald-300"
              aria-label="Previous review"
              data-testid="reviews-sidebar-prev"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              type="button"
              onClick={() => setIdx(safeIdx + 1)}
              className="absolute top-1/2 -right-1 -translate-y-1/2 w-9 h-9 rounded-full bg-white border border-zinc-200 shadow-md flex items-center justify-center text-zinc-600 hover:text-emerald-700 hover:border-emerald-300"
              aria-label="Next review"
              data-testid="reviews-sidebar-next"
            >
              <ChevronRight size={18} />
            </button>
          </>
        )}

        {items.length > 1 && (
          <div className="flex items-center justify-center gap-1.5 mt-3">
            {items.slice(0, 8).map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setIdx(i)}
                aria-label={`Show review ${i + 1}`}
                data-testid={`reviews-sidebar-dot-${i}`}
                className={`h-2 w-2 rounded-full transition-all ${
                  i === (safeIdx % 8)
                    ? "bg-emerald-600"
                    : "bg-zinc-300 hover:bg-zinc-400"
                }`}
              />
            ))}
          </div>
        )}
      </div>

      <div className="border-t border-zinc-100 px-5 py-3 text-center">
        <a
          href={reviewsHref}
          className="text-sm font-semibold text-emerald-700 hover:text-emerald-800"
          data-testid="reviews-sidebar-see-all"
        >
          See all reviews →
        </a>
      </div>
    </div>
  );
}
